import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GeneralService } from '../../services/general.service';
import { skLocalizationCalendar, SYSTEMS } from '../../constants';
import { AppCommunicationService } from '../../services/app-communication.service';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { BtnFilterItemModel } from '../btn-group-filter/btn-filter-item.model';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit {
  applicationDropdown = {
    data: [],
    value: [],
    settings: {
      singleSelection: false,
      idField: 'id',
      textField: 'name',
      selectAllText: 'Označiť všetko',
      unSelectAllText: 'Odznačiť všetko',
      itemsShowLimit: 5,
      allowSearchFilter: false,
    },
  };
  typeDropdown = {
    data: [],
    value: [],
    settings: {
      singleSelection: true,
      idField: 'item_id',
      textField: 'translated',
      selectAllText: 'Označiť všetko',
      unSelectAllText: 'Odznačiť všetko',
      itemsShowLimit: 2,
      allowSearchFilter: false,
    },
  };
  skLocalizationCalendar = skLocalizationCalendar;
  filterItems: BtnFilterItemModel[] = [];

  notificationForm: FormGroup;
  selectedNotification = null;
  notifications = [];
  loading = false;
  visibleAddSection = false;
  selectedSystemFilter = 'all';

  constructor(
    private fb: FormBuilder,
    private generalService: GeneralService,
    private toastr: ToastrService,
    private appCommunicationService: AppCommunicationService,
  ) {
  }

  ngOnInit() {
    this.fulfillApplications();
    this.fulfillTypeDropdown();
    this.generateFilterItems();

    this.getNotifications(false, 'all');

    this.notificationForm = this.fb.group({
      title: ['', [Validators.minLength(1), Validators.maxLength(200)]],
      description: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(2000)]],
      expirationDatetime: [''],
      type: ['', [Validators.required]],
      application: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(200)]],
    });
  }

  generateFilterItems() {
    let id = 1;
    SYSTEMS.forEach(system => {
      const filter = new BtnFilterItemModel();
      filter.id = id;
      filter.title = system.name;
      filter.brdColor = system.brdColor;
      filter.color = system.color;
      filter.txtColor = system.txtColor;
      id++;
      this.filterItems.push(filter);
    });
  }

  getNotifications(isForLoginScreen: boolean, systemName: string) {
    this.generalService
      .getNotifications(isForLoginScreen, systemName)
      .subscribe(val => this.notifications = val);
  }

  onFilterSelectEvent(value: BtnFilterItemModel) {
    if (value && value.title) {
      this.selectedSystemFilter = value.title;
      if (value.title === 'LOGIN') {
        this.getNotifications(true, 'all');
      } else {
        this.getNotifications(false, this.selectedSystemFilter);
      }
    }
  }

  toggleAddSection() {
    if (this.selectedNotification) {
      this.selectedNotification = null;
      this.notificationForm.reset();
      this.applicationDropdown.value = [];
      this.typeDropdown.value = [];
    }
    this.visibleAddSection = !this.visibleAddSection;
  }

  fulfillApplications() {
    this.applicationDropdown.data = SYSTEMS;
  }

  fulfillTypeDropdown() {
    this.typeDropdown.data = [
      {
        item_id: 1,
        item_name: 'info',
        translated: 'Informácia',
      },
      {
        item_id: 2,
        item_name: 'warning',
        translated: 'Upozornenie',
      },
      {
        item_id: 3,
        item_name: 'danger',
        translated: 'Varovanie',
      },
    ];
  }

  onApplicationDropdownChange() {
    const arr = [];

    if (this.applicationDropdown.value.length > 0) {
      this.applicationDropdown
        .value
        .forEach(item => {
          arr.push(item.name);
        });

      this.notificationForm
        .get('application')
        .setValue(arr.toString());
    } else {
      this.notificationForm
        .get('application')
        .setValue('');
    }
  }

  onTypeDropdownChange() {
    if (this.typeDropdown.value.length > 0) {
      this.notificationForm
        .get('type')
        .setValue(
          this.typeDropdown.data.filter(item => item.translated === this.typeDropdown.value[0].translated)[0].item_name,
        );
    } else {
      this.notificationForm
        .get('type')
        .setValue('');
    }
  }

  resetForm() {
    this.notificationForm.reset();
    this.visibleAddSection = false;
    this.applicationDropdown.value = [];
    this.typeDropdown.value = [];
  }

  onNotificationSubmit() {
    if (this.applicationDropdown.value.some(item => item.name === 'LOGIN')) {
      this.notificationForm.value.isForLoginScreen = true;
    }

    if (this.selectedNotification) {
      const id = this.selectedNotification.id;
      this.selectedNotification = this.notificationForm.value;
      this.selectedNotification.id = id;
      if (this.selectedNotification.expirationDatetime) {
        this.selectedNotification.expirationDatetime = moment(this.selectedNotification.expirationDatetime, 'DD.MM.YYYY')
          .format('YYYY-MM-DD HH:mm:ss');
      }
      this.generalService
        .updateNotification(this.selectedNotification)
        .subscribe(() => {
          this.successMessage();
          this.resetForm();
          this.getNotifications(false, this.selectedSystemFilter);
          this.selectedNotification = null;
        }, () => this.errorMessage());
    } else {
      this.generalService
        .addNotification(this.notificationForm.value)
        .subscribe(() => {
          this.successMessage();
          this.resetForm();
          this.getNotifications(false, this.selectedSystemFilter);
        }, () => this.errorMessage());
    }
  }

  onNotificationEdit(notification) {
    this.selectedNotification = notification;
    this.typeDropdown.value = [];
    this.applicationDropdown.value = [];
    this.notificationForm
      .patchValue({
        title: notification.title,
        description: notification.description,
        application: notification.application,
        type: notification.type,
        expirationDatetime: notification.expirationDatetime ? moment(notification.expirationDatetime).format('DD.MM.YYYY') : '',
      });
    this.typeDropdown.value.push(this.typeDropdown.data.filter(item => item.item_name === notification.type)[0]);
    const apps = notification.application.split(',');
    apps.forEach(app => {
      this.applicationDropdown.value.push(this.applicationDropdown.data.filter(item => item.name === app)[0]);
    });
    if (this.visibleAddSection) {
      return;
    }
    this.visibleAddSection = true;
  }

  onNotificationDelete(notification) {
    // this.notificationService.smartMessageBox({
    //   title: 'Odstránenie notifikácie',
    //   content: 'Skutočne chcete odstrániť danú notifikáciu?',
    //   buttons: '[Nie][Áno]',
    // }, (ButtonPressed) => {
    //   if (ButtonPressed === 'Áno') {
    //     this._appCommunicationService.loadingOn('Načítavam notifikácie, počkajte prosím.');
    //     this._generalService
    //       .deleteNotification(notification)
    //       .subscribe(val => {
    //         this.resetForm();
    //         this.getNotifications(false, 'all');
    //         this._appCommunicationService.loadingOff();
    //       });
    //   }
    // });

  }

  /* ##### Alert messages ##### */
  successMessage() {
    this.toastr.success(`Udalosť bola úspešne ${this.selectedNotification ? 'upravená' : 'pridaná'}.`, `${this.selectedNotification ? 'Upravená' : 'Pridaná'}`);
  }

  errorMessage() {
    this.toastr.error(`Vyskytla sa chyba počas ${this.selectedNotification ? 'úpravy' : 'pridávania'} notifikácie.`, 'Chyba');
  }
}
